import { IonContent, IonIcon, IonPopover, isPlatform } from '@ionic/react';
import { arrowRedoOutline, clipboardOutline } from 'ionicons/icons';
import React from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { ToastMsg } from '../../layouts/ToastMsg';
import { createShareUrl } from '../../shared/url-utils';
import { SocialShareSelector } from '../SocialShareSelector';
import { ToolbarButton } from '../ToolbarButton';
import { useLocation } from '../../contexts/LocationContext';

export const ShareDatasourceDetails: React.FC = () => {
  const { network, marker } = useSettings();
  const { viewport, searchedLocation } = useLocation();
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [showToast, setShowToast] = React.useState(false);

  const shareData: ShareData = React.useMemo(
    () => ({
      title: `Check out live air quality, measured for you by ${network?.name}`,
      text: `Check out live air quality at ${marker?.datasourceName}, measured for you by ${network?.name}`,
      url:
        network && marker && viewport
          ? createShareUrl(network.networkId, marker.datasourceId, {
              longitude: marker.location.coordinates[0],
              latitude: marker.location.coordinates[1],
              zoom: viewport.zoom,
              searchedLatitude: searchedLocation?.coordinates[1],
              searchedLongitude: searchedLocation?.coordinates[0],
            })
          : undefined,
    }),
    [marker, network, viewport]
  );
  const shouldUseNativeShare = React.useMemo(() => {
    if (!navigator.canShare) {
      // Web Share API is not supported
      return false;
    }
    if (!navigator.canShare(shareData)) {
      // Share data unsupported, disallowed, or invalid
      return false;
    }
    if (isPlatform('desktop')) {
      // Avoid using the native share on desktop because they don't offer social media sharing
      return false;
    }
    return true;
  }, [shareData]);

  const onShareDatasource = React.useCallback(() => {
    if (shouldUseNativeShare) {
      navigator.share(shareData);
    } else {
      setIsPopoverOpen(true);
    }
  }, [shouldUseNativeShare, shareData]);

  return (
    network &&
    marker && (
      <>
        <ToolbarButton id="share-ds-details" onClick={onShareDatasource}>
          <IonIcon icon={arrowRedoOutline} slot="icon-only" />
        </ToolbarButton>
        {!shouldUseNativeShare && (
          <IonPopover trigger="share-ds-details" isOpen={isPopoverOpen}>
            <IonContent>
              <SocialShareSelector
                shareData={shareData}
                onDidShare={(shareOption) => {
                  setIsPopoverOpen(false);
                  if (shareOption === 'CopyLink') {
                    setShowToast(true);
                  }
                }}
              />
            </IonContent>
          </IonPopover>
        )}
        <ToastMsg
          isOpen={showToast}
          onClose={() => setShowToast(false)}
          type="success"
          message="Copied to clipboard!"
          icon={clipboardOutline}
        />
      </>
    )
  );
};
